import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Package,
  Percent,
  Bike,
  Store,
  Wallet as WalletIcon,
  Info,
  PlayCircle,
  Receipt,
  Route as RouteIcon,
  UserCheck,
  Landmark,
} from 'lucide-react';
import { PageGuard } from '@components/PageGuard';
import { usePermission } from '@hooks/usePermission';
import { CompleteTransactionModal } from '@components/finance/CompleteTransactionModal';
import {
  moneyDashboardService,
  summariseReconciliation,
  toSplitView,
  RIDER_PAYOUT_SOURCE_LABEL,
  type BasketSplit,
  type OrderSplits,
  type SplitBasis,
  type SplitCompletedBy,
  type SplitViewDelivery,
} from '@services/moneyDashboard.service';
import { cn, formatCurrency } from '@lib/utils';
import {
  chipTxnType,
  formatAmount,
  formatRate,
  formatTimestamp,
  labelTxnType,
  moneyEquals,
  resolvePercent,
  shortId,
} from './ledgerFormat';

const inputClass =
  'rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors';

const thClass =
  'text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] whitespace-nowrap';

const tdClass = 'px-6 py-3 text-[14px] text-[var(--text-primary)] whitespace-nowrap';

const sectionCard = 'rounded-3xl bg-[var(--bg-surface)] shadow-soft overflow-hidden';

function errorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

// ── One line of the waterfall ──────────────────────────────────────────────
// The operator has to be able to redo the sum in their head: the sign says what
// the line does to the running figure, the formula says how it was produced,
// the amount is the result. Nothing on this page is a bare number.

type LineTone = 'default' | 'muted' | 'result' | 'deduct' | 'danger';

const toneClass: Record<LineTone, string> = {
  default: 'text-[var(--text-primary)]',
  muted: 'text-[var(--text-secondary)]',
  result: 'text-[var(--text-primary)] font-semibold',
  deduct: 'text-[var(--state-warning)]',
  danger: 'text-[var(--state-danger)] font-semibold',
};

interface LineProps {
  /** `+`, `−`, `=` or a tree glyph — how this line relates to the one above. */
  sign?: string;
  label: string;
  note?: ReactNode;
  formula?: string;
  /** Null means the figure genuinely could not be resolved. It renders as
   *  "unknown", never as 0 — a zero here would read as "nobody is owed anything". */
  amount: number | null;
  tone?: LineTone;
  emphasis?: boolean;
  unknownLabel?: string;
}

const Line = ({
  sign,
  label,
  note,
  formula,
  amount,
  tone = 'default',
  emphasis,
  unknownLabel = 'unknown',
}: LineProps) => (
  <div
    className={cn(
      'flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-[var(--border-subtle)] px-6 py-3 last:border-b-0',
      emphasis && 'bg-[var(--bg-base)]',
    )}
  >
    <div className="flex min-w-0 items-baseline gap-2">
      <span className="w-4 shrink-0 font-mono text-[13px] text-[var(--text-muted)]">
        {sign ?? ''}
      </span>
      <div className="min-w-0">
        <div className={cn('text-[14px]', emphasis ? 'font-semibold' : 'font-medium', toneClass[tone])}>
          {label}
        </div>
        {note && <div className="mt-0.5 text-[12px] text-[var(--text-muted)]">{note}</div>}
      </div>
    </div>

    <div className="flex items-baseline gap-4">
      {formula && (
        <span className="font-mono text-[12px] text-[var(--text-muted)]">{formula}</span>
      )}
      {amount === null ? (
        <span className="whitespace-nowrap text-[13px] italic text-[var(--text-muted)]">
          {unknownLabel}
        </span>
      ) : (
        <span className={cn('whitespace-nowrap text-[14px] tabular-nums', toneClass[tone])}>
          {formatCurrency(amount)}
        </span>
      )}
    </div>
  </div>
);

const ReconcileBadge = ({ ok }: { ok: boolean }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.06em]',
      ok
        ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
        : 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
    )}
  >
    {ok ? (
      <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
    ) : (
      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
    )}
    {ok ? 'Reconciles' : 'Does not reconcile'}
  </span>
);

const BlockHeader = ({
  icon: Icon,
  title,
  subtitle,
  right,
}: {
  icon: typeof Package;
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) => (
  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-6 py-5">
    <div className="flex min-w-0 items-start gap-2.5">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-muted)]" strokeWidth={1.5} />
      <div className="min-w-0 space-y-0.5">
        <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{title}</h3>
        {subtitle && <p className="text-[12px] text-[var(--text-muted)]">{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

/** The release snapshot is free-form JSONB. Render the scalars as a grid and
 *  the nested bits as JSON — never hide it, it is the only record of what was
 *  actually paid out. */
const SnapshotBlock = ({ snapshot }: { snapshot: Record<string, unknown> }) => {
  const entries = Object.entries(snapshot);
  return (
    <div className={sectionCard}>
      <BlockHeader
        icon={Info}
        title="Release snapshot"
        subtitle="Frozen at the moment escrow was released — this is what was actually paid."
      />
      <div className="grid grid-cols-1 gap-x-8 gap-y-3 px-6 py-5 sm:grid-cols-2">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-baseline justify-between gap-4">
            <span className="text-[13px] text-[var(--text-muted)]">
              {key.replace(/_/g, ' ')}
            </span>
            <span className="max-w-[60%] truncate text-right font-mono text-[13px] text-[var(--text-primary)]">
              {typeof value === 'number'
                ? formatAmount(value)
                : value === null || value === undefined
                  ? '—'
                  : typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value)}
            </span>
          </div>
        ))}
        {entries.length === 0 && (
          <p className="text-[13px] text-[var(--text-muted)]">Snapshot is empty.</p>
        )}
      </div>
    </div>
  );
};

// ── Wallet movements — shared by the released and unreleased blocks ──────
const WalletMovementsBlock = ({ basket }: { basket: BasketSplit }) => (
      <div className={sectionCard}>
        <BlockHeader
          icon={WalletIcon}
          title="Wallet movements"
          subtitle="The actual transactions this basket produced. Open a wallet for its full statement."
        />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-base)]">
              <tr>
                <th className={thClass}>When</th>
                <th className={thClass}>Type</th>
                <th className={thClass}>Wallet</th>
                <th className={thClass}>Amount</th>
                <th className={thClass}>Balance after</th>
                <th className={thClass}>Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)]">
              {basket.walletMovements.map((m) => (
                <tr key={m.id} className="transition-colors hover:bg-[var(--bg-base)]">
                  <td className={cn(tdClass, 'text-[var(--text-muted)]')}>
                    {formatTimestamp(m.createdAt)}
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={cn(
                        'whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold',
                        chipTxnType(m.transactionType),
                      )}
                    >
                      {labelTxnType(m.transactionType)}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-[13px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/finance/wallets/${m.walletId}`}
                        className="text-[var(--text-accent)] hover:underline"
                      >
                        {m.walletLabel ?? shortId(m.walletId)}
                      </Link>
                      {m.counterpartyWalletId && (
                        <>
                          <ArrowRight
                            className="h-3.5 w-3.5 text-[var(--text-muted)]"
                            strokeWidth={1.5}
                          />
                          <Link
                            to={`/finance/wallets/${m.counterpartyWalletId}`}
                            className="text-[var(--text-accent)] hover:underline"
                          >
                            {m.counterpartyLabel ?? shortId(m.counterpartyWalletId)}
                          </Link>
                        </>
                      )}
                    </div>
                  </td>
                  <td className={cn(tdClass, 'font-medium')}>{formatCurrency(m.amount)}</td>
                  <td className={tdClass}>{formatCurrency(m.runningBalance)}</td>
                  <td className="px-6 py-3 text-[13px] text-[var(--text-secondary)]">
                    <span className="block max-w-[280px] truncate">{m.description ?? '—'}</span>
                  </td>
                </tr>
              ))}

              {basket.walletMovements.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-[14px] text-[var(--text-muted)]"
                  >
                    No wallet transactions for this basket yet — nothing has moved.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
);

// ── Which set of numbers is on screen ──────────────────────────────────────
// The single most confusing thing about this page used to be that an unreleased
// basket showed nothing at all. It now always shows a full waterfall — so the
// FIRST thing it must say is whether those figures are money that moved or money
// that would move.
const BasisBanner = ({ basis }: { basis: SplitBasis }) => {
  const projected = basis === 'projected';
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-3xl border px-6 py-5',
        projected
          ? 'border-[var(--state-warning-border)] bg-[var(--state-warning-bg)]'
          : 'border-[var(--state-success-border)] bg-[var(--state-success-bg)]',
      )}
    >
      {projected ? (
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]" strokeWidth={1.5} />
      ) : (
        <CheckCircle2
          className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-success)]"
          strokeWidth={1.5}
        />
      )}
      <div className="space-y-1">
        <p className="text-[14px] font-semibold text-[var(--text-primary)]">
          {projected
            ? 'Projected — nothing has been paid out yet; these are the figures that WOULD apply'
            : 'Released — this is what was actually paid'}
        </p>
        <p className="text-[13px] text-[var(--text-secondary)]">
          {projected
            ? 'Recomputed from this basket’s own stored columns plus the live commission and ' +
              'delivery-tax config. The customer’s money is still in escrow, earmarked by the ' +
              'holds below. Settle the basket to turn these into real movements.'
            : 'Frozen onto the journal entry at the moment escrow was released. These numbers ' +
              'are immutable — the wallet movements below are their receipts.'}
        </p>
      </div>
    </div>
  );
};

// ── Who settled it by hand ─────────────────────────────────────────────────
// A manual completion is money moved by a person, not by the buyer. That must be
// attributable forever, on released baskets too.
const CompletedByPanel = ({ completedBy }: { completedBy: SplitCompletedBy }) => (
  <div className="flex items-start gap-3 rounded-3xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-6 py-5">
    <UserCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-accent)]" strokeWidth={1.5} />
    <div className="min-w-0 space-y-1">
      <p className="text-[14px] font-semibold text-[var(--text-primary)]">
        Completed by {completedBy.name ?? shortId(completedBy.userId) ?? 'an internal user'}
        {completedBy.reason ? ` — ${completedBy.reason}` : ''}
      </p>
      <p className="text-[12px] text-[var(--text-muted)]">
        {formatTimestamp(completedBy.at)}
        {completedBy.via ? ` · via ${completedBy.via}` : ''}
        {completedBy.userId ? ` · user ${shortId(completedBy.userId)}` : ''}
      </p>
      <p className="text-[13px] text-[var(--text-secondary)]">
        This basket was not confirmed by the buyer. An internal user released escrow
        manually, running the same split the buyer confirmation would have run.
      </p>
    </div>
  </div>
);

// ── Checkout stored one split, config will pay another ─────────────────────
// Real on live data: the basket carries a rider/Chekit split written at checkout,
// but the release path recomputes from live config. An operator about to settle
// must see the disagreement BEFORE they press the button, not after.
const ConfigMismatchPanel = ({ delivery }: { delivery: SplitViewDelivery }) => {
  const cfg = delivery.configSplit;
  if (!cfg) return null;
  return (
    <div className="flex items-start gap-3 rounded-3xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-6 py-5">
      <AlertTriangle
        className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-danger)]"
        strokeWidth={1.5}
      />
      <div className="min-w-0 space-y-2">
        <p className="text-[14px] font-semibold text-[var(--text-primary)]">
          The stored delivery split is not what a release would pay
        </p>
        <p className="text-[13px] text-[var(--text-secondary)]">
          Checkout stored rider {formatAmount(delivery.riderPayout)} / Chekit{' '}
          {formatAmount(delivery.chekitCommission)}, but the current config will pay rider{' '}
          {formatAmount(cfg.riderPayout)} / Chekit {formatAmount(cfg.chekitCommission)} on
          release. Settling this basket posts the config figures, not the stored ones.
        </p>
        <div className="grid grid-cols-1 gap-x-8 gap-y-1 pt-1 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Stored at checkout
            </div>
            <div className="font-mono text-[12px] text-[var(--text-primary)]">
              rider {formatAmount(delivery.riderPayout)} · chekit{' '}
              {formatAmount(delivery.chekitCommission)} · vat {formatAmount(delivery.vat)}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              What release will pay
            </div>
            <div className="font-mono text-[12px] text-[var(--text-primary)]">
              rider {formatAmount(cfg.riderPayout)} · chekit {formatAmount(cfg.chekitCommission)}{' '}
              · vat {formatAmount(cfg.vat)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A rate is either a percentage (10.4651) or a fraction (0.104651) depending on
 *  the config row that produced it — and it can be missing entirely. */
function ratePercent(
  rate: number | null,
  part: number | null,
  base: number | null,
): number | null {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return null;
  return resolvePercent(rate, part ?? 0, base ?? 0);
}

/** Taxes, stated rather than implied. `unavailable` is a real answer and must
 *  never be flattened to 0 — a 0 here reads as "no VAT is due". */
const TaxPanel = ({
  goodsVat,
  delivery,
}: {
  goodsVat: number | null;
  delivery: SplitViewDelivery;
}) => {
  const unknown = delivery.taxSource === 'unavailable';
  const cell = (label: string, value: ReactNode) => (
    <div>
      <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
        {label}
      </div>
      <div className="mt-1 text-[14px] font-medium text-[var(--text-primary)]">{value}</div>
    </div>
  );
  const unknownText = (
    <span className="text-[13px] italic text-[var(--state-warning)]">
      unknown — tax config not resolvable
    </span>
  );

  return (
    <div className={sectionCard}>
      <BlockHeader
        icon={Receipt}
        title="Taxes"
        subtitle="Goods VAT is embedded in the price and kept by the merchant. Delivery VAT depends on the configured tax model."
      />
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 px-6 py-5 sm:grid-cols-2 lg:grid-cols-4">
        {cell(
          'VAT embedded in goods',
          goodsVat === null ? (
            <span className="text-[13px] italic text-[var(--text-muted)]">unknown</span>
          ) : (
            formatCurrency(goodsVat)
          ),
        )}
        {cell(
          'VAT on delivery',
          unknown || delivery.vat === null ? unknownText : formatCurrency(delivery.vat),
        )}
        {cell(
          'Delivery VAT rate',
          unknown || delivery.vatRate === null ? unknownText : formatRate(delivery.vatRate),
        )}
        {cell(
          'Tax model',
          unknown || !delivery.taxModel || delivery.taxModel === 'unknown' ? (
            unknownText
          ) : (
            <span className="capitalize">{delivery.taxModel.replace(/_/g, ' ')}</span>
          ),
        )}
        {cell(
          'VAT payer',
          unknown || !delivery.vatPayer ? (
            unknownText
          ) : (
            <span className="capitalize">{delivery.vatPayer}</span>
          ),
        )}
        {cell(
          'Tax float allocation',
          delivery.vat === null ? (
            unknownText
          ) : (
            <>
              {formatCurrency(delivery.vat)}
              <span className="ml-2 text-[12px] text-[var(--text-muted)]">
                goods VAT stays with the merchant
              </span>
            </>
          ),
        )}
      </div>
    </div>
  );
};

// ── One basket, fully decomposed ───────────────────────────────────────────
const BasketSplitBlock = ({
  basket,
  focused,
  onComplete,
}: {
  basket: BasketSplit;
  focused: boolean;
  onComplete: (basket: BasketSplit) => void;
}) => {
  // The settle endpoint is gated server-side on chekit-level payments.process;
  // mirror it here so an operator is never shown an action that will 403.
  const { hasCheKitPermission } = usePermission();
  const canSettle = hasCheKitPermission('payments.process');
  // `computed` is null until the basket is RELEASED (the split snapshot is
  // written on release). `projected` is sent for EVERY basket, so the waterfall
  // — the delivery fee especially — renders either way. `basis` says which.
  const released = !!basket.computed;
  const view = toSplitView(basket);

  if (!view) {
    // Neither a release snapshot nor a projection: the API could not read the
    // commission/tax config at all. Say exactly that instead of showing zeroes.
    return (
      <section
        className={cn('space-y-4 rounded-[28px] p-1', focused && 'bg-[var(--state-success-bg)]')}
      >
        <div className={sectionCard}>
          <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 shrink-0 text-[var(--text-muted)]" strokeWidth={1.5} />
                <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                  {basket.businessName ?? 'Unnamed merchant'}
                </h2>
              </div>
              <p className="text-[12px] text-[var(--text-muted)]">
                Basket <span className="font-mono">{shortId(basket.basketId)}</span> ·{' '}
                <span className="capitalize">{basket.status.replace(/_/g, ' ')}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Basket total
              </div>
              <div className="font-display text-[24px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {formatCurrency(basket.totalAmount)}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 border-t border-[var(--border-subtle)] bg-[var(--bg-base)] px-6 py-5">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                No split could be computed for this basket
              </p>
              <p className="text-[13px] text-[var(--text-secondary)]">
                It has never been released, and the commission/delivery-tax config could not
                be read, so there is nothing to project either. The wallet movements below are
                the only reliable record.
              </p>
            </div>
          </div>
        </div>
        {basket.completedBy && <CompletedByPanel completedBy={basket.completedBy} />}
        <WalletMovementsBlock basket={basket} />
      </section>
    );
  }

  const { goods, delivery, commissionParts, allocations, reconciles } = view;

  // Rates arrive as either a percentage or a fraction — pick the reading that
  // reproduces the amount, so the formula on screen is the one applied. Null
  // when the config could not be resolved: "unknown", never 0.
  const goodsRate = ratePercent(goods.commissionRate, goods.commissionAmount, goods.exVatBase);
  const goodsUnknown = goods.commissionSource === 'unavailable';
  const taxUnknown = delivery.taxSource === 'unavailable';

  const merchantNetChecks =
    goods.merchantNet !== null &&
    goods.commissionAmount !== null &&
    moneyEquals(goods.subtotal - goods.commissionAmount, goods.merchantNet);
  const vatSplitChecks =
    goods.exVatBase !== null &&
    goods.embeddedVat !== null &&
    moneyEquals(goods.exVatBase + goods.embeddedVat, goods.subtotal);

  // The delivery fee is fully accounted for when rider + Chekit + VAT == fee.
  const deliveryPartsSum =
    delivery.riderPayout !== null && delivery.chekitCommission !== null && delivery.vat !== null
      ? delivery.riderPayout + delivery.chekitCommission + delivery.vat
      : null;
  const deliveryChecks = deliveryPartsSum !== null && moneyEquals(deliveryPartsSum, delivery.fee);

  const commissionSum =
    commissionParts.fromGoods !== null && commissionParts.fromDelivery !== null
      ? commissionParts.fromGoods + commissionParts.fromDelivery
      : null;
  const commissionChecks =
    commissionSum !== null &&
    commissionParts.total !== null &&
    moneyEquals(commissionSum, commissionParts.total);

  const allocationSum =
    allocations.merchant !== null &&
    allocations.commission !== null &&
    allocations.rider !== null &&
    allocations.tax !== null
      ? allocations.merchant + allocations.commission + allocations.rider + allocations.tax
      : null;
  const allocationChecks =
    allocationSum !== null &&
    allocations.total !== null &&
    moneyEquals(allocationSum, allocations.total);
  const basketChecks =
    allocations.total !== null && moneyEquals(allocations.total, basket.totalAmount);

  // Only worth shouting about before a settlement — after release the money has
  // already gone where it went.
  const showConfigMismatch = !released && delivery.matchesReleasePath === false;

  return (
    <section
      className={cn(
        'space-y-4 rounded-[28px] p-1',
        focused && 'bg-[var(--state-success-bg)]',
      )}
    >
      {/* Basket header */}
      <div className={cn(sectionCard, !reconciles && 'border border-[var(--state-danger-border)]')}>
        <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 shrink-0 text-[var(--text-muted)]" strokeWidth={1.5} />
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                {basket.businessName ?? 'Unnamed merchant'}
              </h2>
              {focused && (
                <span className="rounded-full bg-[var(--state-success-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]">
                  You searched this
                </span>
              )}
            </div>
            <p className="text-[12px] text-[var(--text-muted)]">
              Basket <span className="font-mono">{shortId(basket.basketId)}</span> ·{' '}
              <span className="capitalize">{basket.status.replace(/_/g, ' ')}</span>
              {basket.riderName && ` · delivered by ${basket.riderName}`}
              {basket.businessId && (
                <>
                  {' · '}
                  <Link
                    to={`/finance/owners/business/${basket.businessId}`}
                    className="text-[var(--text-accent)] hover:underline"
                  >
                    merchant ledger
                  </Link>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-4">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Basket total
              </div>
              <div className="font-display text-[24px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {formatCurrency(basket.totalAmount)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <ReconcileBadge ok={reconciles} />
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]',
                  released
                    ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                    : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
                )}
              >
                {released ? 'Released' : 'Projected'}
              </span>
            </div>
            {!released && !canSettle && (
              <span className="text-[12px] text-[var(--text-muted)]">
                Needs <span className="font-mono">payments.process</span> to settle
              </span>
            )}
            {!released && canSettle && (
              <button
                type="button"
                onClick={() => onComplete(basket)}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-medium text-white transition-opacity hover:opacity-90"
              >
                <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                Complete this transaction
              </button>
            )}
          </div>
        </div>
      </div>

      <BasisBanner basis={view.basis} />

      {basket.completedBy && <CompletedByPanel completedBy={basket.completedBy} />}

      {showConfigMismatch && <ConfigMismatchPanel delivery={delivery} />}

      {/* Goods waterfall */}
      <div className={sectionCard}>
        <BlockHeader
          icon={Package}
          title="Goods"
          subtitle="Prices are VAT-inclusive. Commission is charged on the ex-VAT base only — never on the VAT."
        />
        <Line
          label="Goods subtotal"
          note="What the customer paid for the items in this basket"
          amount={goods.subtotal}
          tone="default"
        />
        <Line
          sign="├"
          label="Ex-VAT base"
          formula={`${formatAmount(goods.subtotal)} × 100/116 = ${formatAmount(goods.exVatBase)}`}
          amount={goods.exVatBase}
          tone="muted"
        />
        <Line
          sign="└"
          label="Embedded VAT"
          note="Merchant keeps this and remits it themselves — tax float stays at zero"
          formula={`${formatAmount(goods.subtotal)} × 16/116 = ${formatAmount(goods.embeddedVat)}`}
          amount={goods.embeddedVat}
          tone="muted"
        />
        {goods.exVatBase !== null && goods.embeddedVat !== null && !vatSplitChecks && (
          <Line
            sign="!"
            label="Ex-VAT base + embedded VAT does not equal the subtotal"
            formula={`${formatAmount(goods.exVatBase)} + ${formatAmount(goods.embeddedVat)} = ${formatAmount(goods.exVatBase + goods.embeddedVat)}`}
            amount={goods.exVatBase + goods.embeddedVat - goods.subtotal}
            tone="danger"
          />
        )}
        <Line
          sign="−"
          label={
            goodsUnknown
              ? 'Chekit commission — rate not resolvable'
              : `Chekit commission @ ${formatRate(goodsRate)}`
          }
          note={
            goodsUnknown
              ? goods.commissionUnavailableReason
                ? `Commission config unavailable — ${goods.commissionUnavailableReason}. This is not zero commission; it is an unknown.`
                : 'Commission config unavailable — this is not zero commission; it is an unknown.'
              : `Charged on the ex-VAT base${goods.commissionMode ? ` · ${goods.commissionMode} mode` : ''}`
          }
          formula={
            goodsUnknown
              ? undefined
              : `${formatAmount(goods.exVatBase)} × ${formatRate(goodsRate)} = ${formatAmount(goods.commissionAmount)}`
          }
          amount={goods.commissionAmount}
          tone={goodsUnknown ? 'muted' : 'deduct'}
          unknownLabel="unknown"
        />
        <Line
          sign="="
          label="Merchant net on goods"
          note={
            goods.merchantNet === null
              ? 'Cannot be computed without the commission figure'
              : merchantNetChecks
                ? 'Subtotal minus commission — the merchant keeps the VAT'
                : released
                  ? 'Does not equal subtotal minus commission — check the release snapshot'
                  : 'Does not equal subtotal minus commission — check the commission config'
          }
          formula={
            goods.merchantNet === null
              ? undefined
              : `${formatAmount(goods.subtotal)} − ${formatAmount(goods.commissionAmount)} = ${formatAmount(goods.merchantNet)}`
          }
          amount={goods.merchantNet}
          tone={goods.merchantNet === null ? 'muted' : merchantNetChecks ? 'result' : 'danger'}
          emphasis
        />
      </div>

      {/* Delivery waterfall — always rendered, released or not */}
      <div className={sectionCard}>
        <BlockHeader
          icon={Bike}
          title="Delivery"
          subtitle="The delivery fee splits three ways: the rider's payout, Chekit's cut, and VAT."
          right={
            delivery.providerType ? (
              <span className="rounded-full bg-[var(--bg-base)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                {delivery.providerType.replace(/_/g, ' ')}
              </span>
            ) : undefined
          }
        />
        <Line
          label="Delivery fee"
          note={
            <>
              What the customer paid for delivery on this basket
              {delivery.providerType ? ` · provider: ${delivery.providerType}` : ' · provider not recorded'}
            </>
          }
          amount={delivery.fee}
        />
        <Line
          sign="├"
          label="Rider payout"
          note={
            <>
              What the rider earns on this basket
              {delivery.riderPayoutSource && (
                <span className="block">
                  Source: {RIDER_PAYOUT_SOURCE_LABEL[delivery.riderPayoutSource]}
                </span>
              )}
              {basket.riderName && <span className="block">Rider: {basket.riderName}</span>}
            </>
          }
          amount={delivery.riderPayout}
          tone="result"
        />
        <Line
          sign="├"
          label="Chekit's cut of delivery"
          note="Whatever the fee is not paying the rider or VAT"
          formula={
            delivery.riderPayout === null || delivery.vat === null
              ? undefined
              : `${formatAmount(delivery.fee)} − ${formatAmount(delivery.riderPayout)} − ${formatAmount(delivery.vat)} = ${formatAmount(delivery.chekitCommission)}`
          }
          amount={delivery.chekitCommission}
          tone="deduct"
        />
        <Line
          sign="└"
          label="VAT on delivery"
          note={
            taxUnknown ? (
              'Tax config not resolvable — this is unknown, not zero'
            ) : (
              <>
                Model: {delivery.taxModel ?? 'none recorded'} · Rate:{' '}
                {delivery.vatRate === null ? 'unknown' : formatRate(delivery.vatRate)} · Payer:{' '}
                {delivery.vatPayer ?? 'none recorded'}
              </>
            )
          }
          amount={delivery.vat}
          tone="muted"
          unknownLabel="unknown — tax config not resolvable"
        />
        <Line
          sign="="
          label="Delivery fee accounted for"
          note={
            deliveryPartsSum === null
              ? 'Cannot be checked while part of the split is unknown'
              : deliveryChecks
                ? 'Rider payout + Chekit cut + VAT add back up to the fee'
                : 'These three do not add up to the delivery fee'
          }
          formula={
            deliveryPartsSum === null
              ? undefined
              : `${formatAmount(delivery.riderPayout)} + ${formatAmount(delivery.chekitCommission)} + ${formatAmount(delivery.vat)} = ${formatAmount(deliveryPartsSum)}`
          }
          amount={deliveryPartsSum}
          tone={deliveryPartsSum === null ? 'muted' : deliveryChecks ? 'result' : 'danger'}
          emphasis
        />
        {delivery.chekitServiceFee !== null && (
          <Line
            label="Chekit delivery service fee (stored at checkout)"
            note="What checkout recorded as Chekit's delivery service fee on this basket"
            amount={delivery.chekitServiceFee}
            tone="muted"
          />
        )}
        {!!delivery.businessContribution && (
          <Line
            label="Merchant delivery contribution"
            note="Part of the fee the merchant absorbs — already inside the fee above"
            amount={delivery.businessContribution}
            tone="muted"
          />
        )}
        {!!delivery.chekitContribution && (
          <Line
            label="Chekit delivery subsidy"
            note="Part of the fee Chekit absorbs — already inside the fee above"
            amount={delivery.chekitContribution}
            tone="muted"
          />
        )}
      </div>

      {/* Taxes, stated outright */}
      <TaxPanel goodsVat={goods.embeddedVat} delivery={delivery} />

      {/* Commission decomposition — the point of the whole page */}
      <div
        className={cn(
          sectionCard,
          commissionSum !== null &&
            !commissionChecks &&
            'border border-[var(--state-danger-border)]',
        )}
      >
        <BlockHeader
          icon={Percent}
          title="Commission decomposition"
          subtitle="Chekit's total take on this basket is two different charges at two different rates."
          right={commissionSum === null ? undefined : <ReconcileBadge ok={commissionChecks} />}
        />
        <div className="px-6 py-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-4">
            <div>
              <div className="font-display text-[26px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {commissionParts.fromGoods === null ? (
                  <span className="text-[16px] italic text-[var(--text-muted)]">unknown</span>
                ) : (
                  formatCurrency(commissionParts.fromGoods)
                )}
              </div>
              <div className="mt-1.5 text-[12px] text-[var(--text-muted)]">
                from goods {goodsRate === null ? '(rate unknown)' : `@ ${formatRate(goodsRate)}`}
              </div>
            </div>

            <span className="font-display text-[24px] text-[var(--text-muted)]">+</span>

            <div>
              <div className="font-display text-[26px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {commissionParts.fromDelivery === null ? (
                  <span className="text-[16px] italic text-[var(--text-muted)]">unknown</span>
                ) : (
                  formatCurrency(commissionParts.fromDelivery)
                )}
              </div>
              <div className="mt-1.5 text-[12px] text-[var(--text-muted)]">
                from delivery — the fee less the rider payout and VAT
              </div>
            </div>

            <span className="font-display text-[24px] text-[var(--text-muted)]">=</span>

            <div>
              <div
                className={cn(
                  'font-display text-[26px] font-medium leading-none tracking-[-0.03em]',
                  commissionParts.total === null
                    ? 'text-[var(--text-muted)]'
                    : commissionChecks
                      ? 'text-[var(--text-accent)]'
                      : 'text-[var(--state-danger)]',
                )}
              >
                {commissionParts.total === null ? (
                  <span className="text-[16px] italic">unknown</span>
                ) : (
                  formatCurrency(commissionParts.total)
                )}
              </div>
              <div className="mt-1.5 text-[12px] text-[var(--text-muted)]">total commission</div>
            </div>
          </div>

          <p className="mt-5 border-t border-[var(--border-subtle)] pt-4 font-mono text-[12px] text-[var(--text-muted)]">
            {formatAmount(commissionParts.fromGoods)} + {formatAmount(commissionParts.fromDelivery)}{' '}
            = {formatAmount(commissionSum)}
            {commissionSum !== null && commissionParts.total !== null && !commissionChecks && (
              <span className="ml-2 font-sans font-semibold text-[var(--state-danger)]">
                but the API reports {formatAmount(commissionParts.total)} — off by{' '}
                {formatAmount(commissionParts.total - commissionSum)}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Allocations */}
      <div
        className={cn(
          sectionCard,
          allocations.total !== null &&
            !(allocationChecks && basketChecks && reconciles) &&
            'border border-[var(--state-danger-border)]',
        )}
      >
        <BlockHeader
          icon={Landmark}
          title="Allocations"
          subtitle={
            released
              ? 'Where every shilling of this basket went. These four must add up to the basket total.'
              : 'Where every shilling of this basket would go if it were settled now. These four must add up to the basket total.'
          }
          right={<ReconcileBadge ok={reconciles} />}
        />
        <Line label="Merchant" amount={allocations.merchant} sign=" " tone="muted" />
        <Line label="Chekit commission" amount={allocations.commission} sign="+" tone="muted" />
        <Line
          label="Rider"
          note={
            allocations.riderFoldedIntoCommission
              ? 'Folded into commission — no rider wallet could be resolved'
              : basket.riderName
                ? `Paid to ${basket.riderName}`
                : undefined
          }
          amount={allocations.rider}
          sign="+"
          tone="muted"
        />
        <Line
          label="Tax float"
          note={
            taxUnknown
              ? 'Tax config not resolvable — unknown, not zero'
              : 'Goods VAT stays with the merchant; only delivery VAT lands here'
          }
          amount={allocations.tax}
          sign="+"
          tone="muted"
          unknownLabel="unknown — tax config not resolvable"
        />
        <Line
          sign="="
          label="Allocated total"
          formula={`${formatAmount(allocations.merchant)} + ${formatAmount(allocations.commission)} + ${formatAmount(allocations.rider)} + ${formatAmount(allocations.tax)} = ${formatAmount(allocationSum)}`}
          amount={allocations.total}
          tone={
            allocations.total === null ? 'muted' : allocationChecks ? 'result' : 'danger'
          }
          emphasis
        />
        <Line
          label="Basket total"
          note={
            allocations.total === null
              ? 'Part of the split is unknown, so this cannot be checked'
              : basketChecks
                ? 'Matches the allocated total — the money adds up'
                : `Allocated total is off by ${formatAmount(allocations.total - basket.totalAmount)}`
          }
          amount={basket.totalAmount}
          tone={allocations.total === null ? 'muted' : basketChecks ? 'result' : 'danger'}
          emphasis
        />
      </div>

      {/* Snapshot */}
      {basket.snapshot ? (
        <SnapshotBlock snapshot={basket.snapshot} />
      ) : (
        <div className="flex items-start gap-3 rounded-3xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-6">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-accent)]" strokeWidth={1.5} />
          <div className="space-y-1">
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">
              No release snapshot — this basket has never been released
            </p>
            <p className="text-[13px] text-[var(--text-secondary)]">
              Everything above is computed from this basket&apos;s own columns and the
              commission/delivery config as it stands today. Nothing here has been paid
              out, so there is no frozen record to compare it against. Use “Complete this
              transaction” to release it for real.
            </p>
          </div>
        </div>
      )}

      {/* Wallet movements — the simplest transaction */}
      <WalletMovementsBlock basket={basket} />
    </section>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────
export const OrderSplitPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [input, setInput] = useState(orderId ?? '');
  /** The basket whose settlement modal is open. */
  const [completing, setCompleting] = useState<BasketSplit | null>(null);

  useEffect(() => {
    setInput(orderId ?? '');
  }, [orderId]);

  const { data, isLoading, error } = useQuery<OrderSplits>({
    queryKey: ['money-dashboard', 'splits', orderId],
    queryFn: () => moneyDashboardService.getOrderSplits(orderId!),
    enabled: !!orderId,
    retry: false,
  });

  const notFound = errorStatus(error) === 404;
  const focusBasketId = data?.focusBasketId ?? null;
  // The API nests the order; `orderId` is only a legacy alias.
  const resolvedOrderId = data?.order?.id ?? data?.orderId ?? orderId ?? null;
  const reconciliation = data?.reconciliation;
  // The API sends component facts, not a verdict — derive one so a merely
  // partially-released order is not reported as a reconciliation FAILURE.
  const verdict = summariseReconciliation(reconciliation);

  return (
    <PageGuard permission="wallets.read">
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Finance
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Split drilldown
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            One order, cut down to the arithmetic: what the merchant keeps, what
            Chekit charged on goods and on delivery, what the driver earns, and the
            wallet transactions each of those produced.
          </p>
        </div>

        {/* Id input */}
        <div className="space-y-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = input.trim();
              if (trimmed) navigate(`/finance/splits/${trimmed}`);
            }}
            className="flex flex-wrap items-center gap-3"
          >
            <div className="relative min-w-[280px] max-w-[560px] flex-1">
              <Search
                className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Paste an order id or a basket id..."
                className={cn(inputClass, 'w-full pl-10')}
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-6 py-3 text-[14px] font-medium text-white transition-opacity hover:opacity-90"
            >
              Split it
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
            {orderId && (
              <Link
                to={`/finance/trace/${orderId}`}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] px-5 py-3 text-[14px] font-medium text-[var(--text-primary)] transition-colors hover:border-[var(--border-strong)]"
              >
                <RouteIcon className="h-4 w-4" strokeWidth={1.5} />
                Money trace for this id
              </Link>
            )}
          </form>
          <p className="text-[12px] text-[var(--text-muted)]">
            A basket id resolves up to its parent order — every basket in the order is
            shown, with the one you searched highlighted.
          </p>
        </div>

        {!orderId && (
          <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
            Enter an order or basket id above to break its money down.
          </div>
        )}

        {orderId && isLoading && (
          <div className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}>
            Splitting order {shortId(orderId)}...
          </div>
        )}

        {orderId && !!error && !isLoading && (
          <div className={cn(sectionCard, 'space-y-2 p-12 text-center')}>
            <p className="text-[14px] text-[var(--state-danger)]">
              {notFound
                ? 'No order or basket found with that id.'
                : 'Failed to split this order.'}
            </p>
            {notFound && (
              <p className="text-[13px] text-[var(--text-muted)]">
                Both an order id and a per-merchant basket id resolve here.
              </p>
            )}
          </div>
        )}

        {data && (
          <>
            {/* Searched a basket */}
            {data.resolvedFrom === 'basket' && (
              <div className="flex items-start gap-3 rounded-3xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-6">
                <Info
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-accent)]"
                  strokeWidth={1.5}
                />
                <div className="space-y-1">
                  <p className="text-[14px] font-semibold text-[var(--text-primary)]">
                    You searched a basket — showing every basket in its parent order
                  </p>
                  <p className="text-[13px] text-[var(--text-secondary)]">
                    Basket {shortId(focusBasketId)} belongs to order{' '}
                    {shortId(resolvedOrderId)}. Reconciliation is only meaningful across
                    the whole order.
                  </p>
                </div>
              </div>
            )}

            {/* Order-level reconciliation */}
            {reconciliation && (
              <div
                className={cn(
                  'rounded-3xl bg-[var(--bg-surface)] p-6 shadow-soft',
                  !verdict.reconciles && 'border border-[var(--state-danger-border)]',
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 space-y-1">
                    <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
                      Order reconciliation
                    </h2>
                    <p className="text-[13px] text-[var(--text-muted)]">
                      {verdict.message}
                    </p>
                    <p className="font-mono text-[12px] text-[var(--text-muted)]">
                      Order {shortId(resolvedOrderId)} · {data.baskets.length}{' '}
                      {data.baskets.length === 1 ? 'basket' : 'baskets'}
                      {data.order?.paymentStatus ? ` · payment ${data.order.paymentStatus}` : ''}
                    </p>
                  </div>
                  <ReconcileBadge ok={verdict.reconciles} />
                </div>

                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-[var(--border-subtle)] pt-5 md:grid-cols-4">
                  {(
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Order total
                      </div>
                      <div className="mt-1 text-[15px] font-medium text-[var(--text-primary)]">
                        {formatCurrency(reconciliation.orderTotal)}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Allocated
                    </div>
                    <div className="mt-1 text-[15px] font-medium text-[var(--text-primary)]">
                      {verdict.fullyReleased ? formatCurrency(reconciliation.sumAllocations) : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Difference
                    </div>
                    <div
                      className={cn(
                        'mt-1 text-[15px] font-medium',
                        !verdict.fullyReleased ||
                          moneyEquals(reconciliation.sumAllocations - reconciliation.orderTotal, 0)
                          ? 'text-[var(--text-primary)]'
                          : 'text-[var(--state-danger)]',
                      )}
                    >
                      {verdict.fullyReleased
                        ? formatCurrency(reconciliation.sumAllocations - reconciliation.orderTotal)
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                      Baskets released
                    </div>
                    <div className="mt-1 text-[15px] font-medium text-[var(--text-primary)]">
                      {reconciliation.basketsWithSnapshot} / {reconciliation.basketCount}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Per basket */}
            {data.baskets.map((b) => (
              <BasketSplitBlock
                key={b.basketId}
                basket={b}
                focused={!!focusBasketId && b.basketId === focusBasketId}
                onComplete={setCompleting}
              />
            ))}

            {data.baskets.length === 0 && (
              <div
                className={cn(sectionCard, 'p-12 text-center text-[14px] text-[var(--text-muted)]')}
              >
                This order has no baskets — there is nothing to split.
              </div>
            )}
          </>
        )}

        {completing && (
          <CompleteTransactionModal
            open
            basket={completing}
            orderId={resolvedOrderId}
            onClose={() => setCompleting(null)}
            // Re-fetch this order's split so the basis flips to "Released" and
            // the completed-by panel appears without a manual reload.
            onDone={() =>
              queryClient.invalidateQueries({ queryKey: ['money-dashboard', 'splits'] })
            }
          />
        )}
      </div>
    </PageGuard>
  );
};
